/* useTruthState · Constitutional Refactor · Batch C2b
 * =====================================================
 * The hook that owns the lifecycle of every async user action.
 *
 * Six canonical states — the ONLY values a TruthReceipt may show:
 *   • saved       — durable outcome persisted
 *   • processing  — in-flight, receipt visible
 *   • waiting     — awaiting external actor (approval, verification)
 *   • needs_input — this user must act to continue
 *   • failed      — durable failure, with retry / dismiss actions
 *   • completed   — terminal success (paid, uploaded, verified)
 *
 * Router states (`resolved`, `unrecognised`) live in the Intent
 * Router (C2a) — they are NOT valid TruthReceipt states.
 *
 * Constitutional articles served:
 *   • 3 (Truth Above Convenience) — every action ends knowably.
 *   • 5 (Capability Over Dependency) — users always understand outcomes.
 *   • 7 (Reduce Cognitive Load) — one primitive across the platform.
 *   • 9 (Consistency Builds Trust) — one contract, no bespoke toasts.
 */
import { useCallback, useMemo, useState } from "react";

export const TRUTH_STATE_SAVED       = "saved";
export const TRUTH_STATE_PROCESSING  = "processing";
export const TRUTH_STATE_WAITING     = "waiting";
export const TRUTH_STATE_NEEDS_INPUT = "needs_input";
export const TRUTH_STATE_FAILED      = "failed";
export const TRUTH_STATE_COMPLETED   = "completed";

export const CANONICAL_TRUTH_STATES = Object.freeze([
  TRUTH_STATE_SAVED,
  TRUTH_STATE_PROCESSING,
  TRUTH_STATE_WAITING,
  TRUTH_STATE_NEEDS_INPUT,
  TRUTH_STATE_FAILED,
  TRUTH_STATE_COMPLETED,
]);

export function isCanonicalTruthState(v) {
  return typeof v === "string" && CANONICAL_TRUTH_STATES.includes(v);
}

/**
 * useTruthState(initial?)
 *
 * Returns:
 *   { state, message, setState, run }
 *
 * - `state` — one of the six canonical values or `null`
 * - `message` — optional user-facing sentence (never hallucinated;
 *   the caller supplies it or leaves null)
 * - `setState(state, message?)` — explicit control (throws if the
 *   value isn't canonical — the Truth Doctrine forbids invented states)
 * - `run(fn, { onSuccess, onFailure, waiting })` — canonical wrapper
 *   that transitions processing → completed | failed, catching errors
 *   so nothing silently disappears.
 */
export function useTruthState(initial = null) {
  const [state, _setState] = useState(
    isCanonicalTruthState(initial) ? initial : null,
  );
  const [message, setMessage] = useState(null);

  const setState = useCallback((next, msg = null) => {
    if (next !== null && !isCanonicalTruthState(next)) {
      throw new Error(
        `useTruthState · "${next}" is not a canonical truth state`,
      );
    }
    _setState(next);
    setMessage(msg);
  }, []);

  const run = useCallback(
    async (fn, opts = {}) => {
      const { onSuccess, onFailure, processingMessage, completedMessage, failedMessage } = opts;
      setState(TRUTH_STATE_PROCESSING, processingMessage || null);
      try {
        const result = await fn();
        setState(TRUTH_STATE_COMPLETED, completedMessage || null);
        if (typeof onSuccess === "function") onSuccess(result);
        return result;
      } catch (err) {
        const fallback = err?.response?.data?.detail || err?.message || "Something went wrong";
        setState(TRUTH_STATE_FAILED, failedMessage || fallback);
        if (typeof onFailure === "function") onFailure(err);
        throw err;
      }
    },
    [setState],
  );

  return useMemo(
    () => ({ state, message, setState, run }),
    [state, message, setState, run],
  );
}

export default useTruthState;
